import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import Toast from '../components/common/Toast';
import { authService } from '../services/authService';
import { checkDevModeStatus } from '../services/devModeGuardService';

const LoginPage = () => {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [testCredentials, setTestCredentials] = useState([]);
  const [loadingCredentials, setLoadingCredentials] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(true);



  // Si ya está logueado, redirigir según rol
  if (user) {
    if (user.role === 'admin') return <Navigate to="/admin" />;
    if (user.role === 'coordinator') return <Navigate to="/coordinador" />;
    if (user.role === 'technician') return <Navigate to="/formulario" />;
    if (user.role === 'inspector') return <Navigate to="/inspector" />;
    return <Navigate to="/formulario" />; // Default
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: name === 'username' ? value.toUpperCase() : value
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // ✅ CONVERTIR USERNAME A MAYÚSCULAS antes de enviar
      const usernameUpperCase = formData.username.toUpperCase().trim();

      const response = await login(usernameUpperCase, formData.password);

      // 🛡️ Si es perfil inspector, verificar modo desarrollador en el dispositivo nativo
      if (response && response.user && response.user.role === 'inspector') {
        const devStatus = await checkDevModeStatus();
        if (devStatus.isDevMode || devStatus.isMockLocation) {
          if (logout) {
            try { await logout(); } catch (e) {}
          }
          showToast('⚠️ Desactiva el modo desarrollador de tu celular y loguéate de nuevo.', 'error');
          setLoading(false);
          return;
        }
      }

      showToast(`¡Bienvenido, ${response.user.full_name}!`, 'success');

      setTimeout(() => {
        console.log(' [LoginPage] Redirecting after login...');
        console.log('👤 Current user role:', response.user.role);

        if (response.user.role === 'admin') {
          navigate('/admin');
        } else if (response.user.role === 'coordinator') {
          navigate('/coordinador');
        } else if (response.user.role === 'technician') {
          navigate('/formulario');
        } else if (response.user.role === 'inspector') {
          navigate('/inspector');
        } else {
          navigate('/formulario');
        }
      }, 100);

    } catch (error) {
      console.error('Error de login:', error);

      // Detectar y personalizar mensajes de error de red
      let errorMessage = error.message || 'Error al iniciar sesión';

      // Si el mensaje original era "Failed to fetch" pero no fue interceptado por authService
      if (error.message === 'Failed to fetch' || (error.name === 'TypeError' && error.message.includes('fetch'))) {
        errorMessage = '️ No se pudo conectar con el servidor.\n\nVerifica tu conexión a internet y vuelve a intentar.';
      }

      // Timeout o señal débil
      if (error.message.includes('tardó demasiado') || error.message.includes('señal')) {
        errorMessage = '⚠️ La conexión es lenta o inestable.\n\nBusca un lugar con mejor señal e intenta de nuevo.';
      }

      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestLogin = (credentials) => {
    setFormData({
      username: credentials.username.toUpperCase(), // ✅ Convertir a mayúsculas
      password: credentials.password
    });
    showToast(`Credenciales cargadas para ${credentials.full_name}`, 'info');
  };

  const handleForgotPassword = () => {
    showToast('Contacta a tu coordinador para restablecer tu contraseña', 'info');
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>SISTEMA GESTION MANTENIMIENTO</h1>
          <p>PI / PE</p>
          <div className="version-badge">Versión 2.3.0</div>
        </div>

        {showInstallBanner && (
          <div className="install-banner">
            <button
              className="banner-close"
              onClick={() => setShowInstallBanner(false)}
              aria-label="Cerrar notificación"
            >
              ×
            </button>
            <div className="banner-icon"></div>
            <div className="banner-content">
              <strong>¡Mejora tu experiencia!</strong>
              <p>Instala esta aplicación en tu celular o computadora para un acceso más rápido y cómodo.</p>
              <small>También te pediremos permisos de ubicación para registrar tu recorrido correctamente.</small>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Usuario FFM</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder="Ej: SOLMTCMXT0163"
              style={{ textTransform: 'uppercase' }}
            />
            <small className="form-help">
              Ingresa tu código de usuario FFM asignado
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="form-input password-input"
                placeholder="Ingresa la contraseña"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <small className="form-help">
              RECORRE TU RUTA Y DOCUMENTA LOS SEGMENTOS RECORRIDOS
              <p>cada punto cargado suma a tu distancia</p>
            </small>
          </div>

          <button
            type="submit"
            className="btn btn-primary login-button"
            disabled={loading}
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>

          <button
            type="button"
            className="forgot-password-link"
            onClick={handleForgotPassword}
          >
            {/*¿Olvidaste tu contraseña?*/}
          </button>
        </form>

        {/* Demo credentials section removed */}
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: '', type: '' })}
      />

      <style jsx="true" >{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }

        .login-container {
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          width: 100%;
          max-width: 450px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .login-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .login-header h1 {
          color: #333;
          margin-bottom: 10px;
          font-size: 2rem;
        }

        .login-header p {
          color: #666;
          font-size: 0.9rem;
        }

        .version-badge {
          display: inline-block;
          margin-top: 10px;
          padding: 4px 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: bold;
        }

        .install-banner {
          position: relative;
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          color: white;
          padding: 15px;
          border-radius: 10px;
          margin-bottom: 25px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          box-shadow: 0 4px 15px rgba(245, 87, 108, 0.3);
          animation: slideIn 0.5s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .banner-close {
          position: absolute;
          top: 8px;
          right: 10px;
          background: rgba(255, 255, 255, 0.3);
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }

        .banner-close:hover {
          background: rgba(255, 255, 255, 0.5);
        }

        .banner-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .banner-content {
          flex: 1;
        }

        .banner-content strong {
          display: block;
          font-size: 1rem;
          margin-bottom: 5px;
        }

        .banner-content p {
          font-size: 0.85rem;
          margin: 5px 0;
          line-height: 1.4;
        }

        .banner-content small {
          display: block;
          font-size: 0.75rem;
          opacity: 0.9;
          margin-top: 8px;
          font-style: italic;
        }

        .login-form {
          margin-bottom: 20px;
        }

        .form-help {
          display: block;
          margin-top: 5px;
          font-size: 0.75rem;
          color: #666;
          font-style: italic;
        }

        .login-button {
          width: 100%;
          padding: 12px;
          font-size: 16px;
          margin-bottom: 15px;
        }

        .forgot-password-link {
          background: none;
          border: none;
          color: #007bff;
          cursor: pointer;
          font-size: 14px;
          text-decoration: underline;
          width: 100%;
        }

        .forgot-password-link:hover {
          color: #0056b3;
        }

        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .password-input {
          padding-right: 45px;
        }

        .password-toggle {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #666;
          cursor: pointer;
          padding: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .password-toggle:hover {
          color: #333;
        }

        .password-toggle:focus {
          outline: 2px solid #007bff;
          outline-offset: 2px;
          border-radius: 4px;
        }

        @media (max-width: 480px) {
          .login-container {
            padding: 30px 20px;
            max-width: 95%;
          }
          
          .login-header h1 {
            font-size: 1.5rem;
          }

          .password-toggle {
            padding: 8px;
            right: 5px;
          }

          .install-banner {
            padding: 12px;
          }

          .banner-icon {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;